import React, { useMemo } from "react";
import TableComponent from "../TableComponent";
import TableActionButton from "../TableComponent/TableActionButton";
import EditIcon from "@assets/img/edit-icon.svg";
import CuratorPlaceHolder from "@assets/img/CuratorPlaceHolder.png";
import { UIStatusBadge } from "../StatusBadge";
import { IGuestCuratorRecord } from "@src/types/curator";
import { useSettings } from "@src/context/SettingsContext";
import { UserIcon } from "lucide-react";
import { theme } from "@src/theme";
import CellWithTooltip from "../CellWithTooltip";

const StatusBadge = ({ $status }: { $status: boolean }): React.JSX.Element => {
  return <UIStatusBadge $status={$status ? "primary" : "secondary"} />;
};

type GuestCuratorList = {
  data: IGuestCuratorRecord[];
  onClickEdit: (row: IGuestCuratorRecord) => void;
};
const AboutText = ({
  $compressedUI,
  value,
}: {
  $compressedUI: boolean;
  value: string;
}): React.JSX.Element => {
  return (
    <p
      style={{
        transition: "overflow 0.3s ease, textOverflow 0.3s ease",
        margin: 0,
        maxHeight: "160px",
        textOverflow: "ellipsis",
        whiteSpace: $compressedUI ? "nowrap" : "normal",
        overflow: $compressedUI ? "hidden" : "auto",
        alignContent: "center",
      }}
    >
      {value}
    </p>
  );
};
const CuratorImage = ({
  $compressedUI,
  value,
}: {
  $compressedUI: boolean;
  value: string;
}): React.JSX.Element => {
  if (!value)
    return (
      <div
        style={{
          transition: "width 0.3s ease, height 0.3s ease",
          width: $compressedUI ? "40px" : "110px",
          height: $compressedUI ? "40px" : "110px",
          backgroundColor: theme.colors.yellow,
          borderRadius: theme.margins.small,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <UserIcon
          color={theme.colors.darkGray}
          size={$compressedUI ? 35 : 105}
        />
      </div>
    );
  return (
    <img
      src={value ?? CuratorPlaceHolder}
      style={{
        transition: "width 0.3s ease, height 0.3s ease",
        width: $compressedUI ? "40px" : "110px",
        height: $compressedUI ? "40px" : "110px",
        borderRadius: theme.margins.small,
        objectFit: "cover",
      }}
    />
  );
};
const GuestCuratorList: React.FC<GuestCuratorList> = ({
  data,
  onClickEdit,
}) => {
  const {
    tableSettings: { guestCuratorsTable: compressedUI },
  } = useSettings();
  const columns = useMemo(
    () => [
      {
        Header: "#",
        accessor: "display_row_index",
        width: "40px",
      },
      {
        Header: "Photo",
        accessor: "shareCover",
        hidden: compressedUI,
        Cell: ({ row }: { row: IGuestCuratorRecord }): React.JSX.Element => (
          <CuratorImage
            $compressedUI={compressedUI}
            value={
              row?.webCuratorCatalog?.formats?.thumbnail?.url ||
              row?.webCuratorCatalog?.url
            }
          />
        ),
      },
      {
        Header: "Name",
        accessor: "name",
        Cell: ({ row }: { row: IGuestCuratorRecord }): React.JSX.Element => (
          <CellWithTooltip
            value={row?.name}
            tooltipOptions={{ content: row?.name }}
          />
        ),
      },
      {
        Header: "Show",
        accessor: "shows",
        Cell: ({
          value,
        }: {
          row: any;
          value: { title: string }[];
        }): React.JSX.Element => {
          return (
            <CellWithTooltip
              value={value?.[0]?.title}
              tooltipOptions={{ content: value?.[0]?.title }}
            />
          );
        },
      },
      {
        Header: "About",
        accessor: "about",
        Cell: ({ value }: { value: string }): React.JSX.Element => (
          <AboutText $compressedUI={compressedUI} value={value} />
        ),
      },
      {
        Header: "Status",
        accessor: "active",
        Cell: ({ value }: { value: any }): React.JSX.Element => (
          <StatusBadge $status={value} />
        ),
      },
      {
        Header: "Actions",
        accessor: "actions",
        Cell: ({ row }: { row: IGuestCuratorRecord }): React.JSX.Element => (
          <div style={{ gap: 10, display: "flex", flexDirection: "row" }}>
            <TableActionButton
              alt="edit"
              onClick={() => onClickEdit(row)}
              iconSrc={EditIcon}
            />
          </div>
        ),
      },
    ],
    [compressedUI]
  );
  return (
    <TableComponent
      $compactUI={compressedUI}
      $expandedHeight={compressedUI ? "" : "190px"}
      columns={columns}
      data={data}
    />
  );
};

export default GuestCuratorList;
